export enum ErrorType {
  CLIENT_ERROR = 'ClientError',

  FORM_VALIDATION_ERROR = 'FormValidationError',

  /**
   * Request was cancelled
   */
  REQUEST_CANCELLED = 'RequestCancelled',

  /**
   * Unexpected 5xx error. Probably a temporary backend / proxy issue etc.
   */
  UNEXPECTED_SERVER_ERROR = 'UnexpectedServerError',

  /**
   * 4xx error which has error response in invalid format. The error response
   * should be fixed on the API side to follow the standard error response format.
   */
  UNEXPECTED_CLIENT_ERROR = 'UnexpectedClientError',

  /**
   * Unexpected error which doesn't match any other error criteria
   */
  UNEXPECTED_ERROR = 'UnexpectedError',

  /**
   * 401 response with only message response. example: {"message":"Unauthorized"}
   */
  UNAUTHORIZED = 'Unauthorized',

  PAYLOAD_TOO_LARGE = 'PayloadTooLarge',

  AXIOS_ECONNABORTED = 'ClientSideTimeout',

  AXIOS_ERR_NETWORK = 'NetworkError',

  TWINGATE_BLOCKED = 'TwingateBlocked',
}

export interface ErrorNotification {
  message: string
  timestamp: number

  severity: 'error' | 'info'

  details: { [key: string]: string | undefined }
}

export interface RequestError {
  type: ErrorType

  /**
   * Request Source
   */
  source: string
  /**
   * Time when error was created
   */
  timestamp: number
  /**
   * Raw HTTP details
   */
  details: {
    /**
     * Request URL
     */
    url: string | null

    /**
     * Response data
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    responseData: any

    /**
     * Response status code
     *
     * @example 400
     */
    statusCode: number | null

    /**
     * Request ID from response header X-Outo-Request-Id
     */
    requestId: string

    /**
     * Sentry Trace ID from response header Sentry-Trace
     */
    sentryTrace: string
    /**
     * Stack-trace that lead into creating this RequestError object
     */
    stackTrace: string
  }

  /**
   * Message to be shown to end user
   */
  userMessage: string | null

  /**
   * Verbose message
   *
   * Contains full message we retrieved from API, or for example in case of Axios error this contains
   * Axios's error message
   */
  verboseMessage: string | null

  formValidationProblems: FormValidationProblem[]
}

export interface RequestErrors {
  [key: string]: RequestError | null
}

export interface ApiErrorResponse {
  message: string
  code: string
  title: string
}

export type ApiErrorResponseType = ApiErrorResponse

/*
 *  HTTP response with status code 401 and response content: {"message":"Unauthorized"}
 */
export interface ApiGWErrorResponse {
  message: string
}

export type ApiGWErrorResponseType = ApiGWErrorResponse

/**
 * Form validation error response.
 *
 * Response format is exactly the same as SUPA API address validation response
 * https://github.com/jouzen/outo-cloud/blob/1dd3f11ff15c531440815ddb52ca961d7bb62926/mock_api/supa_api.json#L8048-L8082
 */

export interface FormValidationProblem {
  field?: string | null
  message: string
  corrected?: string | null
}

export interface FormValidationErrorResponse {
  success: boolean
  problems: FormValidationProblem[]
}

export type FormValidationErrorResponseType = FormValidationErrorResponse
